import React from 'react';
import { Alert, Box, Chip, Typography } from '@mui/material';
import FileDropper from '../../Common/FileDropper';
import { uploadCustomerFile } from '../../../support/customerFiles';
import { SalesforceFileAnswer } from '../../../support/assessments';

/**
 * Answer input for a `sf_account_file` question — a file dropper that uploads to Salesforce.
 *
 * The document is stored as a Salesforce File against a specific Account (the secure-customer-file
 * path), so it cannot work until that account is known: `accountId` is resolved from the answer of
 * the `sf_account` question this one is coupled to. With no account chosen yet, the dropper is
 * blocked and the respondent is told which question to fill in first. The answer keeps only the
 * Salesforce reference, never the bytes.
 */

type Props = {
  value: SalesforceFileAnswer | null;
  onChange: (value: SalesforceFileAnswer | null) => void;
  /** The resolved Salesforce AccountId to attach to, or null if none is chosen yet. */
  accountId: string | null;
  /** The name of the account question the respondent must answer first, for the hint. */
  sourceQuestionText?: string;
  disabled?: boolean;
};

const SalesforceAccountFileInput: React.FC<Props> = ({
  value,
  onChange,
  accountId,
  sourceQuestionText,
  disabled,
}) => {
  if (!accountId) {
    return (
      <Alert severity="info">
        {sourceQuestionText
          ? `Select an account in “${sourceQuestionText}” before uploading a file here.`
          : 'Select a Salesforce Account earlier in this assessment before uploading a file here.'}
      </Alert>
    );
  }

  return (
    <Box>
      {value?.file_name && (
        <Chip
          sx={{ mb: 1 }}
          label={value.file_name}
          onDelete={disabled ? undefined : () => onChange(null)}
          variant="outlined"
        />
      )}
      <FileDropper
        title="Upload a document to Salesforce"
        description="Drag & drop a file here, or click to select. Stored on the selected account."
        disabled={disabled}
        upload={({ file }) => uploadCustomerFile(accountId, file)}
        onUploaded={(rec: any) =>
          onChange({
            account_id: accountId,
            content_document_id: rec?.content_document_id,
            content_version_id: rec?.content_version_id,
            file_name: rec?.file_name,
          })
        }
      />
      <Typography variant="caption" color="text.secondary">
        The document is stored as a Salesforce File on the selected account.
      </Typography>
    </Box>
  );
};

export default SalesforceAccountFileInput;
